import { useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { ClientSummary, Fund, FundConfig, FundSurplus } from '../../api/types'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { formatUnits } from './format'
import { useOpsApi } from '../../api/context'
import { apiError } from '../../api/errors'
import { InfoHint } from '../../components/InfoHint'
import { SectionCard } from '../../components/SectionCard'

type AccountKey = 'correctionClientId' | 'successFeeClientId'

const ACCOUNTS: Array<{ key: AccountKey; label: string; description: string }> = [
  {
    key: 'successFeeClientId',
    label: 'Účet „success fee“',
    description:
      'Sem přecházejí jednotky stržené klientům jako výkonnostní odměna (roční zúčtování i odkup). Bez něj nejde success fee potvrdit.',
  },
  {
    key: 'correctionClientId',
    label: 'Účet „opravná položka“',
    description:
      'Drží přebytek jednotek z historie (rozdíl mezi celkem fondu a držbou klientů) jako rezervu.',
  },
]

function optionLabel(client: ClientSummary): string {
  const id = client.personalNumber || client.companyId
  return id ? `${client.fullName} (${id})` : client.fullName
}

interface InternalAccountsCardProps {
  /** Měnit smí jen CEO (BE to vynucuje také), ostatní vidí stav. */
  canEdit: boolean
  /** Fondy modulu — u každého se ukáže přebytek jednotek. */
  funds: Fund[]
  /** Po převodu přebytku — fáze přenačte data. */
  onChanged?: () => void
  /** false = jen nastavení účtů (dorovnání na Excel je samostatný krok startu). */
  showSurplus?: boolean
}

/**
 * Interní účty Apexu (rozhodnutí 13. 10. 2026). Jednotky se při success fee neruší, jen
 * mění držitele: přecházejí z klienta na účet „success fee“. Celek fondu se tím nemění,
 * takže cena jednotky po zúčtování neposkočí. Oba účty jsou běžní klienti v teritoriu
 * Apex s portfoliem; z poplatků a provizí jsou vyřazení.
 */
export function InternalAccountsCard({ canEdit, funds, onChanged, showSurplus = true }: InternalAccountsCardProps) {
  const api = useOpsApi()
  const [config, setConfig] = useState<FundConfig | null>(null)
  const [names, setNames] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .getConfig()
      .then((loaded) => {
        if (!cancelled) setConfig(loaded)
      })
      .catch((err) => {
        if (!cancelled) setError(apiError(err, 'Nastavení účtů se nepodařilo načíst'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [api])

  // Jména nastavených účtů (config nese jen id).
  useEffect(() => {
    if (!config) return
    let cancelled = false
    for (const account of ACCOUNTS) {
      const id = config[account.key]
      if (id && !names[id]) {
        api
          .getClient(id)
          .then((client) => {
            if (!cancelled) setNames((prev) => ({ ...prev, [id]: client.fullName }))
          })
          .catch(() => undefined)
      }
    }
    return () => {
      cancelled = true
    }
  }, [api, config, names])

  const save = async (key: AccountKey, clientId: string) => {
    setError(null)
    try {
      setConfig(await api.updateConfig({ [key]: clientId }))
    } catch (err) {
      setError(apiError(err, 'Účet se nepodařilo uložit'))
    }
  }

  return (
    <SectionCard
      title="Účty Apexu"
      info={
        <InfoHint
          title="K čemu účty jsou"
          lines={[
            'Success fee se strhává v jednotkách. Jednotky nezanikají, přejdou na účet „success fee“, celkový počet jednotek fondu zůstane stejný.',
            'Opravná položka drží přebytek jednotek z historie jako rezervu.',
            'Oba účty jsou klienti v teritoriu Apex s portfoliem. Management fee, success fee ani provize se z nich nepočítají.',
          ]}
          sx={{ color: '#fff' }}
        />
      }
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {config &&
            ACCOUNTS.map((account) => (
              <AccountRow
                key={account.key}
                label={account.label}
                description={account.description}
                clientId={config[account.key]}
                clientName={config[account.key] ? names[config[account.key] as string] : undefined}
                canEdit={canEdit}
                onSave={(id) => save(account.key, id)}
              />
            ))}
          {config && showSurplus && (
            <SurplusSection
              funds={funds}
              canEdit={canEdit}
              hasAccount={Boolean(config.correctionClientId)}
              onChanged={onChanged}
            />
          )}
        </Box>
      )}
    </SectionCard>
  )
}

export interface SurplusSectionProps {
  /** true = bez vlastního nadpisu (vložené do karty, která ho už má). */
  hideHeading?: boolean
  funds: Fund[]
  canEdit: boolean
  hasAccount: boolean
  onChanged?: () => void
}

/**
 * Přebytek jednotek per fond = celek fondu − Σ držeb všech držitelů. Při startu modulu
 * je to rozdíl mezi celkem z fondového Excelu a držbou klientů načtenou z core. Převede
 * se na účet „opravná položka“ za cenu posledního ocenění; celek fondu se nemění.
 */
export function SurplusSection({ funds, canEdit, hasAccount, onChanged, hideHeading }: SurplusSectionProps) {
  const api = useOpsApi()
  const [rows, setRows] = useState<FundSurplus[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<FundSurplus | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  // Čísla z fondového Excelu per fond (text z inputu): celek, SF Apexu z minulých zúčtování, NAV.
  const [excel, setExcel] = useState<Record<string, string>>({})
  const [sfText, setSfText] = useState<Record<string, string>>({})
  const [navText, setNavText] = useState<Record<string, string>>({})
  const parse = (text: string | undefined): number | null => {
    const raw = (text || '').replace(/\s/g, '').replace(',', '.')
    const n = raw ? Number(raw) : NaN
    return Number.isFinite(n) && n > 0 ? n : null
  }
  const excelOf = (key: string): number | null => parse(excel[key])
  const sfOf = (key: string): number => parse(sfText[key]) ?? 0
  const surplusOf = (r: FundSurplus): number => {
    const target = excelOf(r.fundKey)
    const base = target != null ? target - r.positionsUnits : r.surplus
    return Math.round((base - sfOf(r.fundKey)) * 10000) / 10000
  }
  /** Cena pro zápis: z ocenění fondu, jinak NAV z Excelu / celek z Excelu. */
  const priceOf = (r: FundSurplus): number | null => {
    if (r.price != null) return r.price
    const nav = parse(navText[r.fundKey])
    const total = excelOf(r.fundKey)
    return nav != null && total != null ? Math.round((nav / total) * 1e8) / 1e8 : null
  }

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      setRows(await Promise.all(funds.map((f) => api.getSurplus(f.key))))
    } catch (err) {
      setError(apiError(err, 'Přebytek jednotek se nepodařilo spočítat'))
    } finally {
      setLoading(false)
    }
  }

  const handleAssign = async () => {
    if (!confirm) return
    setSubmitting(true)
    setConfirmError(null)
    try {
      const after = await api.assignSurplus(
        confirm.fundKey,
        excelOf(confirm.fundKey) ?? undefined,
        sfOf(confirm.fundKey) || undefined,
        confirm.price == null ? priceOf(confirm) ?? undefined : undefined
      )
      setRows((prev) => prev.map((r) => (r.fundKey === after.fundKey ? after : r)))
      setConfirm(null)
      if (onChanged) onChanged()
    } catch (err) {
      setConfirmError(apiError(err, 'Převod přebytku selhal'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1 }}>
          {!hideHeading && <Typography variant="subtitle2">Dorovnání na celek z Excelu</Typography>}
          <Typography variant="body2" color="text.secondary">
            Zadejte celek fondu podle Excelu a jednotky success fee z minulých zúčtování (ty jdou na účet
            „success fee“). Zbytek proti držbám klientů se připíše na „opravnou položku“ a celek fondu
            bude sedět na Excel. Bez ocenění zadejte NAV z Excelu, cena se dopočte.
          </Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={load} disabled={loading || funds.length === 0}>
          {loading ? 'Počítám…' : 'Spočítat přebytek'}
        </Button>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {rows.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fond</TableCell>
                <TableCell align="right">Celek fondu</TableCell>
                <TableCell align="right">Držby</TableCell>
                <TableCell align="right">Celek podle Excelu</TableCell>
                <TableCell align="right">SF Apexu (minulá zúčtování)</TableCell>
                <TableCell align="right">→ opravná položka</TableCell>
                <TableCell align="right">Cena</TableCell>
                {canEdit && <TableCell align="right" />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.fundKey}>
                  <TableCell>{r.fundKey}</TableCell>
                  <TableCell align="right">{formatUnits(r.totalUnits)}</TableCell>
                  <TableCell align="right">{formatUnits(r.positionsUnits)}</TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      value={excel[r.fundKey] || ''}
                      onChange={(e) => setExcel((prev) => ({ ...prev, [r.fundKey]: e.target.value }))}
                      placeholder="z Excelu"
                      disabled={!canEdit}
                      sx={{ width: 170 }}
                      inputProps={{ style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      value={sfText[r.fundKey] || ''}
                      onChange={(e) => setSfText((prev) => ({ ...prev, [r.fundKey]: e.target.value }))}
                      placeholder="0"
                      disabled={!canEdit}
                      sx={{ width: 140 }}
                      inputProps={{ style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: surplusOf(r) < 0 ? 'error.main' : undefined }}
                  >
                    {formatUnits(surplusOf(r))}
                  </TableCell>
                  <TableCell align="right">
                    {r.price != null ? (
                      formatUnits(r.price)
                    ) : (
                      <TextField
                        size="small"
                        value={navText[r.fundKey] || ''}
                        onChange={(e) => setNavText((prev) => ({ ...prev, [r.fundKey]: e.target.value }))}
                        placeholder="NAV z Excelu"
                        helperText={priceOf(r) != null ? `cena ${formatUnits(priceOf(r) as number)}` : ' '}
                        disabled={!canEdit}
                        sx={{ width: 160 }}
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <Button
                        size="small"
                        disabled={!hasAccount || surplusOf(r) < 0 || (surplusOf(r) === 0 && sfOf(r.fundKey) === 0) || priceOf(r) == null}
                        onClick={() => setConfirm(r)}
                      >
                        Převést
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {rows.some((r) => surplusOf(r) < 0) && (
        <Alert severity="warning">
          Záporný přebytek = klienti drží víc jednotek, než je celek fondu. Zkontrolujte celek fondu
          (Opravit ručně) a načtení držby.
        </Alert>
      )}
      {!hasAccount && rows.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Pro převod nejdřív nastavte účet „opravná položka“.
        </Typography>
      )}
      <ConfirmDialog
        open={Boolean(confirm)}
        title={`Převést přebytek ${confirm ? confirm.fundKey : ''}`}
        text={
          confirm
            ? `${sfOf(confirm.fundKey) > 0 ? `Na účet „success fee“ se připíše ${formatUnits(sfOf(confirm.fundKey))} jednotek. ` : ''}Na účet „opravná položka“ se připíše ${formatUnits(surplusOf(confirm))} jednotek za cenu ${
                priceOf(confirm) != null ? formatUnits(priceOf(confirm) as number) : ''
              }.${
                excelOf(confirm.fundKey) != null
                  ? ` Celek fondu se nastaví na ${formatUnits(excelOf(confirm.fundKey) as number)} jednotek podle Excelu.`
                  : ' Celkový počet jednotek fondu se nemění.'
              } Peníze se nepohybují.`
            : ''
        }
        confirmLabel="Převést"
        doubleConfirm
        submitting={submitting}
        error={confirmError}
        onConfirm={handleAssign}
        onClose={() => {
          setConfirm(null)
          setConfirmError(null)
        }}
      />
    </Box>
  )
}

interface AccountRowProps {
  label: string
  description: string
  clientId: string | null
  clientName?: string
  canEdit: boolean
  onSave: (clientId: string) => Promise<void>
}

function AccountRow({ label, description, clientId, clientName, canEdit, onSave }: AccountRowProps) {
  const api = useOpsApi()
  const [editing, setEditing] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [options, setOptions] = useState<ClientSummary[]>([])
  const [selected, setSelected] = useState<ClientSummary | null>(null)
  const [searching, setSearching] = useState(false)
  const [saving, setSaving] = useState(false)

  // Debounced hledání klienta (vzor NewRedemptionCard).
  useEffect(() => {
    if (!editing) return
    let cancelled = false
    setSearching(true)
    const timer = setTimeout(() => {
      api
        .searchClients(searchInput)
        .then((found) => {
          if (!cancelled) setOptions(found)
        })
        .catch(() => {
          if (!cancelled) setOptions([])
        })
        .finally(() => {
          if (!cancelled) setSearching(false)
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [api, searchInput, editing])

  const handleSave = async () => {
    if (!selected) return
    setSaving(true)
    await onSave(selected.id)
    setSaving(false)
    setEditing(false)
    setSelected(null)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1, minWidth: 240 }}>
          <Typography variant="subtitle2">{label}</Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: clientId ? 'text.primary' : 'warning.main' }}
        >
          {clientId ? `${clientName || 'klient'} (id ${clientId})` : 'nenastaveno'}
        </Typography>
        {canEdit && !editing && (
          <Button size="small" variant="outlined" onClick={() => setEditing(true)}>
            {clientId ? 'Změnit' : 'Nastavit'}
          </Button>
        )}
      </Box>
      {editing && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 320, flexGrow: 1 }}
            options={options}
            value={selected}
            onChange={(_event, value) => setSelected(value)}
            inputValue={searchInput}
            onInputChange={(_event, value) => setSearchInput(value)}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={searching}
            noOptionsText="Žádný klient nenalezen"
            loadingText="Hledám…"
            filterOptions={(x) => x}
            renderInput={(params) => <TextField {...params} label="Klient (APEX Capital a.s. – …)" />}
          />
          <Button variant="contained" size="small" disabled={!selected || saving} onClick={handleSave}>
            Uložit
          </Button>
          <Button size="small" onClick={() => setEditing(false)} disabled={saving}>
            Zrušit
          </Button>
        </Box>
      )}
    </Box>
  )
}
